import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, computeOpenStatus } from "@/lib/api";
import PdfViewer from "@/components/PdfViewer";
import ActionBar from "@/components/ActionBar";
import Gallery from "@/components/Gallery";
import { useLang, LanguageSwitcher } from "@/i18n/LanguageContext";
import { Phone, MapPin, Clock, Info, AlertTriangle, ShoppingBag, Star, ExternalLink, Lock } from "lucide-react";

const GoldRule = () => (
  <div className="gd-divider my-5" aria-hidden="true">
    <span className="text-[#d4af37] text-xs tracking-widest">✦</span>
  </div>
);

const MenuPage = () => {
  const { t, lang } = useLang();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get("/config")
      .then((r) => setConfig(r.data))
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <p className="font-serif text-2xl text-[#d4af37]">
          {t("loadError")}
        </p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-serif text-3xl text-[#d4af37] gd-pulse">
          Giardino del Gusto
        </p>
      </div>
    );
  }

  const { open, today } = computeOpenStatus(config.hours);
  const todayLabel = today
    ? today.closed
      ? t("todayClosed")
      : `${t("today")} ${today.open}–${today.close === "00:00" ? "24:00" : today.close}`
    : "";
  const phoneTel = `tel:+39${(config.phone || "").replace(/\D/g, "")}`;

  return (
    <div className="max-w-xl mx-auto pb-24 relative">
      <LanguageSwitcher className="fixed top-3 right-3 z-50 bg-[#060a14]/80 backdrop-blur-md" />
      {/* Header */}
      <header
        data-testid="site-header"
        className="pt-10 pb-6 px-6 text-center relative overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/34155535/pexels-photo-34155535.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative gd-fade-up">
          {config.has_logo ? (
            <img
              src={`${API}/logo?v=${config.logo_version || 0}`}
              alt={config.name}
              data-testid="site-logo"
              className="h-24 w-auto mx-auto mb-4 object-contain drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]"
            />
          ) : null}
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#d4af37]/80 mb-1">
            {config.subtitle}
          </p>
          {!config.has_logo && (
            <h1
              data-testid="site-name"
              className="font-serif text-5xl sm:text-6xl font-600 text-[#d4af37] leading-none tracking-tight"
            >
              {config.name}
            </h1>
          )}
          <span className="sr-only" data-testid="site-name">
            {config.name}
          </span>
          <GoldRule />
          <p className="font-serif italic text-xl text-[#e0ddcf]">
            “{config.payoff}”
          </p>
        </div>
      </header>

      {/* Info row */}
      <div
        data-testid="info-row"
        className="mx-6 mb-5 flex items-center justify-between gap-3 text-sm border-y border-[#d4af37]/20 py-3"
      >
        <div className="flex items-center gap-2" data-testid="open-status">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: open ? "#4ade80" : "#f87171" }}
          />
          <div className="leading-tight">
            <span
              className="font-semibold"
              style={{ color: open ? "#4ade80" : "#f87171" }}
            >
              {open ? "Aperto ora" : "Chiuso"}
            </span>
            <span className="block text-[11px] text-[#e0ddcf]/70 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {todayLabel}
            </span>
          </div>
        </div>
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            config.maps_query || config.address
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-right text-[#e0ddcf]/85 text-[11px] max-w-[55%] active:text-[#d4af37]"
        >
          <MapPin className="w-3.5 h-3.5 text-[#d4af37] shrink-0" />
          <span className="leading-tight">{config.address}</span>
        </a>
      </div>

      {/* Takeout banner */}
      <a
        href={phoneTel}
        data-testid="takeout-banner"
        className="mx-6 mb-8 block rounded-md bg-[#d4af37] text-[#060a14] px-5 py-4 active:scale-[0.98] transition-transform shadow-[0_4px_20px_rgba(212,175,55,0.25)]"
      >
        <div className="flex items-center gap-3">
          <ShoppingBag className="w-6 h-6 shrink-0" strokeWidth={2} />
          <div>
            <p className="text-[11px] uppercase tracking-[0.15em] font-semibold opacity-80">
              {config.takeout_text || "Servizio d'asporto — ordina al"}
            </p>
            <p className="font-serif text-2xl font-700 flex items-center gap-2 leading-none mt-0.5">
              <Phone className="w-4 h-4" /> {config.phone}
            </p>
          </div>
        </div>
      </a>

      {/* Menu title */}
      <div className="px-6 mb-4 text-center">
        <h2 className="font-serif text-3xl text-[#d4af37]">{t("ourMenu")}</h2>
        {config.menu_updated_at && (
          <p className="text-[11px] text-[#e0ddcf]/50 mt-1">
            {t("updatedOn")}{" "}
            {new Date(config.menu_updated_at).toLocaleDateString(
              lang === "en" ? "en-GB" : "it-IT",
              { day: "2-digit", month: "long", year: "numeric" }
            )}
          </p>
        )}
      </div>

      {/* PDF viewer */}
      <div className="px-6">
        <PdfViewer version={config.pdf_version} hasPdf={config.has_pdf} />
      </div>

      {/* Da sapere */}
      <section data-testid="da-sapere" className="px-6 mt-10">
        <div className="bg-[#0f172a] border-l-2 border-[#d4af37] rounded-r-md p-5">
          <h3 className="font-serif text-2xl text-[#d4af37] flex items-center gap-2 mb-3">
            <Info className="w-5 h-5" /> {t("daSapere")}
          </h3>
          <ul className="space-y-2">
            {(config.notes || []).map((n, i) => (
              <li
                key={i}
                data-testid={`note-${i}`}
                className="text-sm text-[#e0ddcf] flex gap-2"
              >
                <span className="text-[#d4af37] mt-0.5">◆</span>
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>

        <div
          data-testid="allergen-note"
          className="mt-4 flex gap-3 text-xs text-[#e0ddcf]/70 items-start px-1"
        >
          <AlertTriangle className="w-4 h-4 text-[#d4af37] shrink-0 mt-0.5" />
          <p>{config.allergen_note}</p>
        </div>
      </section>

      {(config.google_review_url || config.tripadvisor_url) && (
        <section data-testid="reviews-section" className="px-6 mt-10 text-center">
          <div className="gd-divider mb-4" aria-hidden="true">
            <Star className="w-4 h-4" fill="currentColor" />
          </div>
          <h3 className="font-serif text-2xl text-[#d4af37] mb-1">
            {t("reviewsTitle")}
          </h3>
          <p className="text-xs text-[#e0ddcf]/70 mb-4">
            {t("reviewsSubtitle")}
          </p>
          <div className="flex flex-col gap-3">
            {config.google_review_url && (
              <a
                href={config.google_review_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-review-button"
                className="inline-flex items-center justify-center gap-2 bg-[#d4af37] text-[#060a14] font-semibold px-6 py-3 rounded-sm active:scale-95 transition-transform w-full"
              >
                <Star className="w-4 h-4" fill="currentColor" /> {t("googleReview")}
                <ExternalLink className="w-3.5 h-3.5 opacity-70" />
              </a>
            )}
            {config.tripadvisor_url && (
              <a
                href={config.tripadvisor_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tripadvisor-button"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#d4af37] text-[#d4af37] px-6 py-3 rounded-sm hover:bg-[#d4af37]/10 active:bg-[#d4af37]/20 transition-colors w-full font-medium"
              >
                <Star className="w-4 h-4" /> {t("tripadvisorReview")}
                <ExternalLink className="w-3.5 h-3.5 opacity-70" />
              </a>
            )}
          </div>
        </section>
      )}

      <Gallery />

      <footer className="px-6 mt-10 mb-2 text-center">
        <GoldRule />
        <p className="text-[11px] text-[#e0ddcf]/40 tracking-wide">
          {config.name} · {config.address_note}
        </p>
        <Link
          to="/admin"
          data-testid="admin-access-link"
          className="inline-flex items-center gap-1.5 mt-4 text-[11px] text-[#d4af37]/60 hover:text-[#d4af37] transition-colors tracking-wide"
        >
          <Lock className="w-3 h-3" /> Area riservata · Accesso staff
        </Link>
      </footer>

      <ActionBar config={config} />
    </div>
  );
};

export default MenuPage;
